import _ from "lodash";
import { PARTICIPANT_USER_TYPES } from "./CoursesConstants";

/**
 * Builds a school -> grade -> courses hierarchy from Phase 1 query edges.
 *
 * @param {Array} edges - Course edges from getOrganizationCourseIdsHierarchyQuery
 * @returns {{ hierarchy: Object, allSchoolIds: string[], allGradeIds: string[], allCourseIds: string[] }}
 */
export const buildHierarchyFromEdges = (edges = []) => {
  const hierarchy = {};
  const schoolIdSet = new Set();
  const gradeIdSet = new Set();
  const allCourseIds = [];

  _.forEach(edges, edge => {
    const course = _.get(edge, "node", {});
    const courseId = course.id;
    const schoolId = _.get(course, "curriculumProgram.id");
    const grades = _.get(course, "grades", []);

    if (!courseId || !schoolId) return;

    allCourseIds.push(courseId);
    schoolIdSet.add(schoolId);

    if (!hierarchy[schoolId]) {
      hierarchy[schoolId] = {};
    }

    _.forEach(grades, grade => {
      const gradeId = grade.id;
      if (!gradeId) return;

      gradeIdSet.add(gradeId);

      if (!hierarchy[schoolId][gradeId]) {
        hierarchy[schoolId][gradeId] = [];
      }

      hierarchy[schoolId][gradeId].push(courseId);
    });

    // Handle courses with no grades
    if (_.isEmpty(grades)) {
      const ungradedKey = `${schoolId}_ungraded`;
      if (!hierarchy[schoolId][ungradedKey]) {
        hierarchy[schoolId][ungradedKey] = [];
      }
      hierarchy[schoolId][ungradedKey].push(courseId);
    }
  });

  return {
    hierarchy,
    allSchoolIds: Array.from(schoolIdSet),
    allGradeIds: Array.from(gradeIdSet),
    allCourseIds,
  };
};

/**
 * Returns filtered hierarchy for a set of school IDs, or the full hierarchy
 * when selectedSchoolIds is null (All Schools mode).
 *
 * @param {Object} hierarchy - Full hierarchy map
 * @param {null|Set<string>} selectedSchoolIds - null = all schools, Set = specific selection
 * @returns {Object} Filtered hierarchy
 */
export const getFilteredHierarchy = (hierarchy, selectedSchoolIds) => {
  if (selectedSchoolIds === null) return hierarchy;
  if (selectedSchoolIds.size === 0) return {};
  return _.pickBy(hierarchy, (_grades, schoolId) =>
    selectedSchoolIds.has(schoolId)
  );
};

/**
 * Returns course IDs belonging only to the selected schools.
 * When selectedSchoolIds is null (All Schools), returns allCourseIds as-is.
 *
 * @param {Object} hierarchy - Full hierarchy map { schoolId -> { gradeId -> [courseIds] } }
 * @param {null|Set<string>} selectedSchoolIds - null = all schools, Set = specific selection
 * @param {string[]} allCourseIds - All course IDs (returned unchanged when no school filter)
 * @returns {string[]} Course IDs for selected schools only
 */
export const getFilteredSchoolCourseIds = (
  hierarchy,
  selectedSchoolIds,
  allCourseIds
) => {
  if (selectedSchoolIds === null) return allCourseIds;
  if (selectedSchoolIds.size === 0) return [];

  const ids = [];
  selectedSchoolIds.forEach(schoolId => {
    const grades = hierarchy[schoolId];
    if (!grades) return;
    _.forEach(grades, courseIds => {
      ids.push(...courseIds);
    });
  });
  return _.uniq(ids);
};

export const USER_COUNT_KEYS = {
  STUDENT: "studentCount",
  STAFF: "staffCount",
  FAMILY_MEMBER: "familyCount",
};
/**
 * Formats entity count subtitle (e.g., "25 students, 3 staff, 40 family").
 *
 * @param {Object} countData - Count data object with staffCount, studentCount, familyCount
 * @param {string[]} selectedUserTypes - Array of user types to display
 * @param {Function} t - Translation function
 * @returns {string} Formatted subtitle
 */
export const computeEntityCountSubtitle = (countData, selectedUserTypes, t) => {
  if (_.isEmpty(countData)) return "";

  return _.map(selectedUserTypes, (userType, index) => {
    const countKey = USER_COUNT_KEYS[userType];
    const count = _.get(countData, countKey, 0);
    const label = t(PARTICIPANT_USER_TYPES[userType], { count });
    return `${index ? ", " : ""}${count} ${label}`;
  }).join("");
};

/**
 * Filters course IDs by search text, matching against course titles in the details map.
 *
 * @param {string[]} allCourseIds - All course IDs
 * @param {Object} courseDetailsMap - Map of courseId -> { title, ... }
 * @param {string} searchText - Search string
 * @returns {string[]} Filtered course IDs
 */
export const filterCoursesBySearch = (
  allCourseIds,
  courseDetailsMap,
  searchText
) => {
  if (!searchText) return allCourseIds;

  const lowerSearch = searchText.toLowerCase();

  return _.filter(allCourseIds, id => {
    const title = _.get(courseDetailsMap, `${id}.title`, "");
    return title.toLowerCase().includes(lowerSearch);
  });
};
